import { Button } from '@/components/elements/forms/buttons';
import StarIcon from '@/components/elements/redesign/StarIcon/StarIcon';
import { classnames } from '@/helpers/general';
import { roundRating } from '@/helpers/reviews';
import { customTwMerge } from '@/helpers/theme';
import { _t } from '@/locale';

const baseTranslationKey = 'components.elements.Rating';

export type RatingV2Props = {
  averageRating: number;
  ratingCount?: number;
  reviewCount?: number;
  size: 'sm' | 'md' | 'lg';
  direction?: 'row' | 'column';
  withAverageScore?: boolean;
  withRatingCounter?: boolean;
  withMultipleStars?: boolean;
  withLowRatingCountInformation?: boolean;
  /** Turns the count into a link. Never affects what is visible. */
  onReviewsClick?: () => void;
  className?: string;
};

const SCORE_TEXT_CLASS = { sm: 'text-m', md: 'text-l', lg: 'text-l' } as const;
const COUNT_TEXT_CLASS = { sm: 'text-m', md: 'text-m', lg: 'text-l' } as const;

/** A star lit `fill` percent (0–100) from the left, drawn over an unlit grey star. */
const Star = ({ fill, disabled }: { fill: number; disabled?: boolean }) => (
  <span className="w-md relative">
    <StarIcon className="bg-black-300" />
    {!disabled && <StarIcon className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - fill}% 0 0)` }} />}
  </span>
);

export const RatingV2 = ({
  averageRating,
  ratingCount,
  reviewCount,
  size,
  direction = 'row',
  withAverageScore,
  withRatingCounter,
  withMultipleStars = true,
  withLowRatingCountInformation,
  onReviewsClick,
  className,
}: RatingV2Props) => {
  const countLabel = (() => {
    if (reviewCount > 0) {
      return _t('reviewsFromRatingsCounts', { reviews: reviewCount, ratings: ratingCount });
    }
    if (ratingCount > 0) {
      return `${ratingCount} ${_t(`${baseTranslationKey}.reviews`)}`;
    }
    return _t(`${baseTranslationKey}.notEnoughReviews`);
  })();

  const score = withAverageScore && (
    <span className={classnames('text-fg_primary font-bold', SCORE_TEXT_CLASS[size])}>
      {roundRating(averageRating, 1).toFixed(1)}
    </span>
  );

  const stars = withMultipleStars ? (
    <span className="gap-2xs flex">
      {[1, 2, 3, 4, 5].map((starNumber) => (
        <Star key={starNumber} fill={Math.min(Math.max((averageRating - (starNumber - 1)) * 100, 0), 100)} />
      ))}
    </span>
  ) : (
    <Star fill={100} disabled={!withAverageScore} />
  );

  const getCounter = () => {
    if (withRatingCounter && onReviewsClick) {
      return (
        <Button
          onClick={onReviewsClick}
          variant="link"
          className={classnames('text-fg_link whitespace-nowrap !p-0 underline', COUNT_TEXT_CLASS[size])}>
          {countLabel}
        </Button>
      );
    }

    if (withRatingCounter) {
      return (
        <span className={classnames('text-fg_secondary whitespace-nowrap', COUNT_TEXT_CLASS[size])}>{countLabel}</span>
      );
    }
    return null;
  };

  const count = getCounter();

  const scoreAndStars = (
    <span className="gap-2xs flex select-none items-center">
      {direction === 'row' && !withMultipleStars ? (
        <>
          {stars}
          {score}
        </>
      ) : (
        <>
          {score}
          {stars}
        </>
      )}
    </span>
  );

  if (direction === 'column') {
    return (
      <div className={customTwMerge(classnames('gap-2xs flex flex-col items-end', className))}>
        <div className="gap-xs flex items-center">
          {scoreAndStars}
          {withLowRatingCountInformation && !averageRating ? (
            <p className="text-fg_secondary text-m">{_t('notEnoughReviews')}</p>
          ) : null}
        </div>
        {count}
      </div>
    );
  }

  return (
    <div className={customTwMerge(classnames('gap-xs flex items-center', className))}>
      {scoreAndStars}
      {withLowRatingCountInformation && !averageRating ? (
        <p className="text-fg_secondary text-m">{_t('notEnoughReviews')}</p>
      ) : null}
      {count && (
        <>
          <span className="h-xxs w-xxs bg-fg_secondary rounded-full" />
          {count}
        </>
      )}
    </div>
  );
};
